import { createPublicClient, custom, defineChain, http, parseAbi, encodeFunctionData, parseUnits, zeroAddress, type Address, type Hex } from "viem";
import { launchChain, pairs, type LaunchToken } from "../api/launchpad";
import { configuredElysiumRpcUrl } from "../wallet/elysium-rpc";
import type { Eip1193Provider } from "../wallet/eip1193";
import { quoteLaunchpadSell } from "./launchpad-quoter";

export const launchContracts = {
  launchpad: "0xd65450c291065c60d95c58592df9c776cbc96d8f",
  launchManager: "0x88fa38ead03927d820d729294e41a12e71d0b105",
  lockerImplementation: "0xf2e43b83520f5571bfab3605145d047caa380340",
  locker: "0x595c2c799b95165008f432137f14226c74f1ad1a",
  router: "0x6364ebe66c7f83a17e759035ffcd90a5fed216f2",
  sniperHook: "0x28b78e5d449aa27bb7445999a9d44d25ce2e0044",
  adapter: "0xf285ed7a48fd962fd4a24b536a2b63da0c7b3782",
} as const;
export const launchRpcUrl = "https://rpc.merklewood.fun/";
// TokenCreated.stockPair is a contract flag, not the API's display category.
// KNTQ is grouped as "stock" by the API but emits stockPair=false on chain.
export const launchPairStockFlags = {
  0: false, // HYPE
  1: false, // KNTQ
  2: true, // NVDAx
} as const;
export function createLaunchReadClient(provider?: Eip1193Provider, signal?: AbortSignal) {
  const configuredUrl = configuredElysiumRpcUrl();
  return createPublicClient({
    chain: defineChain({ id: launchChain, name: "Elysium", nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
      rpcUrls: { default: { http: [launchRpcUrl] } } }),
    // Public displays can read without a wallet. Connected preflight falls back
    // to the wallet only when no RPC is configured, never after an HTTP failure.
    transport: !configuredUrl && provider ? custom(provider, { retryCount: 0 }) :
      http(configuredUrl?.href ?? launchRpcUrl, { batch: { wait: 0 }, timeout: 12_000, retryCount: 0,
        fetchOptions: { signal: signal ?? null } }),
  });
}

export const launchRpc = createLaunchReadClient();

export const launchpadAbi = parseAbi([
  "error ExemptWalletLimit()",
  "error AlreadyConfigured()",
  "error Unauthorized()",
  "function createToken(string name,string symbol) returns (address token)",
  "function createToken(string name,string symbol,uint8 pairId) returns (address token)",
  "function launchAndBuy(string name,string symbol,uint8 pairId,uint256 minQuoteOut,uint256 minMemeOut) payable returns (address token,uint256 memeOut)",
  "function createToken(string name,string symbol,uint8 pairId,address[] exemptWallets) returns (address token)",
  "function launchAndBuy(string name,string symbol,uint8 pairId,uint256 minQuoteOut,uint256 minMemeOut,address[] exemptWallets) payable returns (address token,uint256 memeOut)",
  "function buy(address token,uint256 minQuoteOut,uint256 minMemeOut) payable returns (uint256 memeOut)",
  "function quoteTokenForPair(uint8 pairId) view returns (address)",
  "function effectiveQuoteTargetForPair(uint8 pairId) view returns (uint256)",
  "function poolKey(address token,address quoteToken) view returns ((address currency0,address currency1,uint24 fee,int24 tickSpacing,address hooks) key)",
  "function launches(address token) view returns (address creator,uint8 pairId,uint256 positionTokenId,bytes32 v4PoolId,uint160 initialSqrtPriceX96)",
  "event TokenCreated(address indexed token,address indexed creator,string name,string symbol,uint8 pairId,address quoteToken,bool stockPair,bytes32 poolId,uint256 positionTokenId,uint160 sqrtPriceX96,uint256 tokenBurned)",
]);

export const routerAbi = parseAbi([
  "function buyWithNative((address currency0,address currency1,uint24 fee,int24 tickSpacing,address hooks) key,address quoteToken,uint256 minQuoteOut,uint256 minMemeOut,address recipient) payable returns (uint256 memeOut)",
  "function sellForNative((address currency0,address currency1,uint24 fee,int24 tickSpacing,address hooks) key,address quoteToken,uint256 memeAmountIn,uint256 minQuoteOut,uint256 minNativeOut,address recipient) returns (uint256 nativeOut)",
]);

export const quoteAdapterAbi = parseAbi([
  "function quotePerNativeX18(address quoteToken) view returns (uint256)",
  "function swapExactNativeForToken(address quoteToken,address recipient,uint256 minAmountOut) payable returns (uint256 amountOut)",
]);

export const erc20Abi = parseAbi([
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner,address spender) view returns (uint256)",
  "function approve(address spender,uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
]);

export type LaunchCall = { to: Address; data: Hex; value: bigint; expectsTrue?: boolean; verifyMinedCall?: true; antiSniperPool?: Hex };
export type LaunchTerms = { call: LaunchCall; expected?: bigint; minimum?: bigint };
export function inputAmount(input: string) {
  if (!/^\d+(\.\d{1,18})?$/.test(input.trim())) throw new Error("Enter a decimal amount with up to 18 decimal places.");
  const value = parseUnits(input.trim(), 18);
  if (value <= 0n || value >= 2n ** 256n) throw new Error("Amount is out of range.");
  return value;
}
export function minimumOut(output: bigint, bps: bigint) {
  if (bps < 0n || bps >= 10_000n) throw new Error("Invalid slippage.");
  const minimum = output * (10_000n - bps) / 10_000n;
  if (minimum <= 0n) throw new Error("Output is too small.");
  return minimum;
}

async function firstHop(account: Address, id: 0 | 1 | 2, value: bigint, bps: bigint, rpc = launchRpc) {
  if (!id) return 0n;
  const { result } = await rpc.simulateContract({ account, address: launchContracts.adapter, abi: quoteAdapterAbi,
    functionName: "swapExactNativeForToken", args: [pairs[id].address, account, 0n], value });
  return minimumOut(result, bps);
}

type PairId = 0 | 1 | 2;
export type DevBuyTerms = { account: Address; id: PairId; value: bigint; minQuote: bigint; expected: bigint; minimum: bigint };

async function verifyLaunchPair(id: PairId, rpc = launchRpc) {
  if ((await rpc.readContract({ address: launchContracts.launchpad, abi: launchpadAbi, functionName: "quoteTokenForPair", args: [id] })).toLowerCase() !== pairs[id].address) {
    throw new Error("Pair does not match the Launchpad contract.");
  }
}

export async function quoteDevBuy(account: Address, name: string, symbol: string, id: PairId, value: bigint,
  signal?: AbortSignal, exemptWallets: Address[] = []): Promise<DevBuyTerms> {
  const rpc = signal ? createLaunchReadClient(undefined, signal) : launchRpc;
  await verifyLaunchPair(id, rpc);
  signal?.throwIfAborted();
  const minQuote = await firstHop(account, id, value, 100n, rpc);
  signal?.throwIfAborted();
  // Identity is required by the simulation, but it does not determine swap terms.
  const { result: [, expected] } = await rpc.simulateContract({ account, address: launchContracts.launchpad, abi: launchpadAbi,
    functionName: "launchAndBuy", args: [name || "Quote", symbol || "QUOTE", id, minQuote, 0n, exemptWallets], value });
  return { account, id, value, minQuote, expected, minimum: minimumOut(expected, 100n) };
}

export async function prepareLaunch(account: Address, name: string, symbol: string, id: PairId, value: bigint,
  quote?: DevBuyTerms, rpc = launchRpc, exemptWallets: Address[] = []): Promise<LaunchCall> {
  await verifyLaunchPair(id, rpc);
  const to = launchContracts.launchpad;
  if (value === 0n) return { to, value, data: encodeFunctionData({ abi: launchpadAbi, functionName: "createToken", args: [name, symbol, id, exemptWallets] }) };
  if (!quote || quote.account !== account || quote.id !== id || quote.value !== value) throw new Error("Dev buy quote does not match this launch.");
  return { to, value, data: encodeFunctionData({ abi: launchpadAbi, functionName: "launchAndBuy",
    args: [name, symbol, id, quote.minQuote, quote.minimum, exemptWallets] }) };
}

export type SwapToken = Pick<LaunchToken, "address" | "creator" | "pairId" | "quoteAddress" | "poolId" | "positionTokenId">;
export async function verifyMembership(token: SwapToken) {
  const [creator, id, position, pool] = await launchRpc.readContract({ address: launchContracts.launchpad,
    abi: launchpadAbi, functionName: "launches", args: [token.address] });
  if (creator.toLowerCase() !== token.creator.toLowerCase() || id !== token.pairId ||
      position !== BigInt(token.positionTokenId) || pool.toLowerCase() !== token.poolId.toLowerCase()) {
    throw new Error("Token does not match the Launchpad contract.");
  }
}
export async function allowance(account: Address, token: Address) {
  return launchRpc.readContract({ address: token, abi: erc20Abi, functionName: "allowance", args: [account, launchContracts.router] });
}
export function approval(token: Address, amount: bigint): LaunchTerms {
  return { call: { to: token, value: 0n, expectsTrue: true, data: encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [launchContracts.router, amount] }) } };
}

// Preview never supplies sell calldata: approval and execution remain separate.
export async function sellApprovalTerms(account: Address, token: SwapToken, amount: bigint, bps: bigint): Promise<LaunchTerms> {
  await verifyMembership(token);
  const terms = approval(token.address, amount);
  // Two-hop preview acceptance is still pending. Preserve router quoting there.
  if (token.pairId !== 0 || token.quoteAddress.toLowerCase() !== zeroAddress) return terms;
  const blockNumber = await launchRpc.getBlockNumber({ cacheTime: 0 });
  const { fee } = await readAntiSniper(token.poolId as Hex, account, blockNumber);
  // The standalone quoter does not reproduce wallet exemptions during decay.
  if (fee !== 0) return terms;
  const key = await sellPoolKey(token, blockNumber);
  const expected = await quoteLaunchpadSell(launchRpc, key, token.address, token.quoteAddress, amount, blockNumber);
  return { ...terms, expected, minimum: minimumOut(expected, bps) };
}

async function sellPoolKey(token: SwapToken, blockNumber?: bigint) {
  const key = await launchRpc.readContract({ address: launchContracts.launchpad, abi: launchpadAbi, functionName: "poolKey",
    args: [token.address, token.quoteAddress], blockNumber });
  const [currency0, currency1] = [token.address.toLowerCase(), token.quoteAddress.toLowerCase()].sort();
  if (key.currency0.toLowerCase() !== currency0 || key.currency1.toLowerCase() !== currency1 ||
      key.fee !== 10_000 || key.tickSpacing !== 200 || key.hooks.toLowerCase() !== launchContracts.sniperHook) {
    throw new Error("Pool key does not match the Launchpad token.");
  }
  return key;
}

export async function swapTerms(account: Address, token: SwapToken, direction: "buy" | "sell", amount: bigint, bps: bigint): Promise<LaunchTerms> {
  await verifyMembership(token);
  await readAntiSniper(token.poolId as Hex, account);
  if (direction === "buy") {
    const to = launchContracts.launchpad;
    const minQuote = await firstHop(account, token.pairId, amount, bps);
    const { result: expected } = await launchRpc.simulateContract({ account, address: to, abi: launchpadAbi, functionName: "buy",
      args: [token.address, minQuote, 0n], value: amount });
    const minimum = minimumOut(expected, bps);
    return { expected, minimum, call: { to, value: amount, antiSniperPool: token.poolId as Hex, data: encodeFunctionData({ abi: launchpadAbi, functionName: "buy",
      args: [token.address, minQuote, minimum] }) } };
  }
  const to = launchContracts.router;
  const key = await sellPoolKey(token);
  const { result: expected } = await launchRpc.simulateContract({ account, address: to, abi: routerAbi, functionName: "sellForNative",
    args: [key, token.quoteAddress, amount, 0n, 0n, account] });
  const minimum = minimumOut(expected, bps);
  const native = token.quoteAddress.toLowerCase() === zeroAddress;
  return { expected, minimum, call: { to, value: 0n, antiSniperPool: token.poolId as Hex, data: encodeFunctionData({ abi: routerAbi, functionName: "sellForNative",
    args: [key, token.quoteAddress, amount, native ? minimum : 0n, native ? 0n : minimum, account] }) } };
}

export const sniperAbi = parseAbi([
  "function currentSniperFeeBps(bytes32 poolId) view returns (uint16)",
  "function isFeeExempt(bytes32 poolId,address account) view returns (bool)",
]);
export async function readAntiSniper(poolId: Hex, account?: Address, blockNumber?: bigint) {
  const [fee, exempt] = await Promise.all([
    launchRpc.readContract({ address: launchContracts.sniperHook, abi: sniperAbi, functionName: "currentSniperFeeBps", args: [poolId], blockNumber }),
    account ? launchRpc.readContract({ address: launchContracts.sniperHook, abi: sniperAbi, functionName: "isFeeExempt", args: [poolId, account], blockNumber }) : false,
  ]);
  if (!Number.isInteger(fee) || fee < 0 || fee > 9900 || typeof exempt !== "boolean") throw new Error("Invalid anti-sniper response.");
  return { fee: Number(fee), exempt };
}
